import React, { useEffect, useState } from 'react';
import { useOffer } from '../../../context/offersContext/OfferContextProvider';
import { useTermsAndConditions } from '../../../context/termsAndConditionContext/TermsAndConditionContextProvider';
import { useProposal } from '../../../context/proposalContext/ProposalContextProvider'; // Assuming useProposal is in your context
import { Button, showAlert } from '../../../components';
import { FaSave, FaPrint, FaEye } from 'react-icons/fa'; // Import icons
import {
  ModalProvider,
  useModal,
} from '../../../components/common/modal/ModalContext';
import PreviewQuote from '../preview/PreviewQuote';
import { useNavigate } from 'react-router-dom';
import { tr } from 'date-fns/locale';
import ProposalModel from '../../../model/proposal/Proposal.model';

const OffersTerm = () => {
  const { getOffers, offerRes } = useOffer();
  const { getAllTermsAndConditions, termsData } = useTermsAndConditions();
  const {
    proposal,
    setProposal,
    CreateProposal,
    updateProposal,
    initialState,
    handleSaveAndPrint,
    isYesSelected,
    setIsYesSelected,
    handleUpdateAndPrint,
  } = useProposal(); // Assuming you have setProposal in your context
  const { openModal, closeModal } = useModal();
  const navigate = useNavigate();
  const { quoteProducts } = proposal; // Assuming quoteProducts is part of the proposal object

  useEffect(() => {
    getOffers();
    getAllTermsAndConditions();

    setSelectedOffers(proposal.offers || []);
    setSelectedTerms(proposal.termAndConditions || []);

    return () => {
      // Cleanup function to prevent memory leaks
      setSelectedOffers([]);
      setSelectedTerms([]);
    };
  }, []);

  const [selectedOffers, setSelectedOffers] = useState(proposal.offers || []);
  const [selectedTerms, setSelectedTerms] = useState(
    proposal.termAndConditions || [],
  );

  // Handle offers selection
  const handleOfferChange = (offer) => {
    const updatedOffers = selectedOffers.some((o) => o.id === offer.id)
      ? selectedOffers.filter((o) => o.id !== offer.id)
      : [...selectedOffers, offer];

    setSelectedOffers(updatedOffers);
    setProposal((prevProposal) => ({
      ...prevProposal,
      offers: updatedOffers,
    }));
  };

  // Handle terms selection
  const handleTermChange = (term) => {
    const updatedTerms = selectedTerms.some((t) => t.id === term.id)
      ? selectedTerms.filter((t) => t.id !== term.id)
      : [...selectedTerms, term];

    setSelectedTerms(updatedTerms);
    setProposal((prevProposal) => ({
      ...prevProposal,
      termAndConditions: updatedTerms,
    }));
  };

  const handleSave = async (proposal) => {
    // Wait for the state to update before proceeding

    try {
      if (
        proposal.state === 'PIPELINE' ||
        proposal.state === 'WON' ||
        proposal.state === 'LOST'
      ) {
        try {
          await updateProposal(proposal);
          navigate('/vieweditquote');
          setIsYesSelected(true);
        } catch (error) {
          return;
        }
      } else {
        setProposal((prevProposal) => ({
          ...prevProposal,
          state: 'PIPELINE',
        }));
        try {
          await updateProposal({ ...proposal, state: 'PIPELINE' });
          navigate('/vieweditquote');
          setIsYesSelected(true);
        } catch (error) {
          return;
        }
      }
      setProposal(initialState);
    } catch (error) {
      showAlert('error', 'Failed to save proposal');
    }
  };

  const SaveAndPrint = async () => {
    try {
      if (
        proposal.state === 'PIPELINE' ||
        proposal.state === 'WON' ||
        proposal.state === 'LOST'
      ) {
        await handleUpdateAndPrint(proposal);
        navigate('/vieweditquote');
        setIsYesSelected(true);
      } else {
        await handleUpdateAndPrint(proposal);
        navigate('/vieweditquote');
        setIsYesSelected(true);
      }
    } catch (error) {
      showAlert('error', 'Failed to save and print proposal');
    }
  };

  const handlePreview = () => {
    openModal(<PreviewQuote closeModal={closeModal} />, 'Preview Quote');
  };

  return (
    <div className="container mx-auto mt-2">
      <div className="flex justify-between h-[500px] mx-8">
        {/* Offers Section */}
        <div className="w-3/5 p-2 bg-white shadow-lg border border-gray-200 rounded-lg mr-4  overflow-y-auto">
          <h3 className="text-lg font-semibold mb-4 text-gray-700">
            Select Offers
          </h3>
          <div className="grid grid-cols-3 gap-4">
            {offerRes?.map((offerItem) => (
              <label
                key={offerItem.id}
                className={`relative block cursor-pointer p-2 border-2 rounded-lg shadow-sm hover:shadow-md transition-shadow ${
                  selectedOffers.some((o) => o.id === offerItem.id)
                    ? 'border-yellow-500 bg-yellow-100'
                    : 'border-gray-300'
                }`}>
                <input
                  type="checkbox"
                  checked={selectedOffers.some((o) => o.id === offerItem.id)}
                  onChange={() => handleOfferChange(offerItem)}
                  className="absolute top-2 right-2 h-5 w-5 text-yellow-500 focus:ring-yellow-500"
                />
                <img
                  src={offerItem.imageUrl || offerItem.imagePath} // Use either imageUrl or imagePath
                  alt={offerItem.offer}
                  className="w-full h-20 object-cover rounded-md mb-4"
                />
                <span className="block text-gray-800 font-semibold text-center">
                  {offerItem.offer}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Terms and Conditions Section */}
        <div className="w-2/5 p-4 bg-white shadow-lg border border-gray-200 rounded-lg  overflow-y-auto">
          <h3 className="text-lg font-semibold mb-4 text-gray-700">
            Select Terms and Conditions
          </h3>
          <div className="space-y-4">
            {termsData?.map((termItem) => (
              <label
                key={termItem.id}
                className="flex items-center p-4 bg-gray-100 rounded-md shadow-sm hover:bg-gray-200 transition-colors cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedTerms.some((t) => t.id === termItem.id)}
                  onChange={() => handleTermChange(termItem)}
                  className="h-5 w-5 text-yellow-500 focus:ring-yellow-500 mr-3"
                />
                <span className="text-gray-700 font-500">
                  {termItem.termCondition}
                </span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Buttons Section */}
      <div className="flex justify-center mt-10 space-x-6">
        {quoteProducts.some((product) => product.alternateProduct) && (
          <div className="flex flex-col items-center">
            <div className="relative inline-flex items-center h-8 rounded-full w-20 bg-gray-300">
              <span
                className={`${
                  isYesSelected ? 'translate-x-0 ' : 'translate-x-full'
                } inline-block w-10 h-8 transform bg-green-500 rounded-full transition-transform duration-300 ease-in-out`}></span>
              <button
                className={`${
                  isYesSelected ? 'text-white' : 'text-gray-700'
                } absolute left-0 w-10 h-8 rounded-full focus:outline-none`}
                onClick={() => setIsYesSelected(true)}>
                Yes
              </button>
              <button
                className={`${
                  !isYesSelected ? 'text-white bg-red-400' : 'text-gray-700 '
                } absolute right-0 w-10 h-8 rounded-full focus:outline-none`}
                onClick={() => setIsYesSelected(false)}>
                No
              </button>
            </div>
            <small className="font-400 mt-2">Save with Alternative</small>
          </div>
        )}
        <Button
          className="px-6 py-2 bg-gradient-to-r from-yellow-400 to-yellow-500 text-white font-semibold rounded-md hover:from-yellow-500 hover:to-yellow-600 transition-all flex items-center gap-2"
          onClick={() => handleSave(proposal)}>
          <FaSave />
          {proposal.state === 'WON' ||
          proposal.state === 'LOST' ||
          proposal.state === 'PIPELINE'
            ? 'Update'
            : 'Save'}
        </Button>
        <Button
          className="px-6 py-2 bg-gradient-to-r from-yellow-400 to-yellow-500 text-white font-semibold rounded-md hover:from-yellow-500 hover:to-yellow-600 transition-all flex items-center gap-2"
          onClick={() => SaveAndPrint()}>
          <FaPrint />
          {proposal.state === 'WON' ||
          proposal.state === 'LOST' ||
          proposal.state === 'PIPELINE'
            ? 'Update & Print'
            : 'Save & Print'}
        </Button>
        <Button
          className="secondary flex items-center gap-2"
          onClick={handlePreview}>
          <FaEye />
          Preview
        </Button>
      </div>
    </div>
  );
};

const App = () => (
  <ModalProvider>
    <OffersTerm />
  </ModalProvider>
);

export default App;
